// En förfrågan som widgeten skickar in (ADR 0041). Schemat delas av widgeten
// och API:t. Kontaktuppgifterna hålls isär från svaren, eftersom bara de är
// personuppgifter och sparas i en egen tabell som kan gallras.
import { z } from 'zod';

/** Längsta fritext. Längre text är sällan en riktig förfrågan och kostar mer att tolka. */
export const MAX_FREE_TEXT = 2000;

/**
 * Står i stället för en text som har gallrats efter byråns gallringstid
 * (steg 11.1, ADR 0115), där fältet inte får vara tomt.
 */
export const PURGED_TEXT = '(gallrad)';

/** Fält som är personuppgifter och därför inte får ligga bland svaren. */
const personfalt = ['name', 'email', 'phone', 'companyname', 'orgnumber', 'personalnumber'];

const svarsvarde = z.union([
  z.string().trim().max(500),
  z.number().finite(),
  z.boolean(),
  z.null(),
  z.array(z.string().trim().max(200)).max(20),
]);

export const inquiryAnswersSchema = z
  .record(
    z.string().regex(/^[a-zA-Z][a-zA-Z0-9]{0,49}$/, 'Fältnamnet skrivs i camelCase'),
    svarsvarde,
  )
  .refine((svar) => Object.keys(svar).length <= 40, 'Högst 40 svar')
  .superRefine((svar, ctx) => {
    for (const falt of Object.keys(svar)) {
      if (personfalt.includes(falt.toLowerCase())) {
        ctx.addIssue({
          code: 'custom',
          path: [falt],
          message: 'Personuppgifter skickas i contact, inte bland svaren',
        });
      }
    }
  });

export const inquiryContactSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.email().max(254),
  phone: z.string().trim().min(5).max(30).optional(),
  companyName: z.string().trim().min(1).max(150).optional(),
  /**
   * Organisationsnumret, om byrån frågar efter det (ADR 0070). För en enskild
   * firma är det personnumret, så det hör till kontaktuppgifterna.
   */
  orgNumber: z
    .string()
    .trim()
    .regex(/^(\d{2})?\d{6}-?\d{4}$/, 'Skriv organisationsnumret som 556123-4567')
    .optional(),
});

export const inquirySubmissionSchema = z.object({
  /** En förfrågan om offert, eller en vanlig fråga till byrån. */
  kind: z.enum(['quote', 'question']),
  answers: inquiryAnswersSchema,
  freeText: z.string().trim().max(MAX_FREE_TEXT).optional(),
  contact: inquiryContactSchema,
  /** Kunden godkänner att byrån kontaktar dem. Utan samtycke sparas inget. */
  consent: z.literal(true, { error: 'Samtycke krävs för att byrån ska kunna svara' }),
  /** Sidan på byråns webbplats där formuläret fylldes i. */
  sourceUrl: z.url().max(500).optional(),
  /**
   * Ett dolt fält som människor inte ser och därför lämnar tomt. Är det
   * ifyllt är avsändaren troligen en bot.
   */
  website: z.string().max(500).optional(),
  /**
   * Svaret från botkontrollen i webbläsaren (Cloudflare Turnstile, ADR 0085).
   * Kontrolleras av API:t och sparas aldrig.
   */
  botToken: z.string().max(2048).optional(),
});

export type InquirySubmission = z.infer<typeof inquirySubmissionSchema>;
